import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUpload, FiImage, FiAlertCircle, FiCheckCircle, FiX } = FiIcons;

const MAX_FILE_SIZE = 400 * 1024; // 400KB in bytes
const ALLOWED_FORMATS = ['image/jpeg', 'image/png', 'image/jpg'];

const LogoUploader = ({ currentImageUrl, onImageChange }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(currentImageUrl || '');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef(null);

  // Update preview when currentImageUrl changes from parent
  React.useEffect(() => {
    if (currentImageUrl !== preview) {
      setPreview(currentImageUrl || '');
    }
  }, [currentImageUrl]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      validateAndProcessFile(file);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      validateAndProcessFile(file);
    }
  };

  const validateAndProcessFile = (file) => {
    setError(null);
    setUploadSuccess(false);

    // Check file format
    if (!ALLOWED_FORMATS.includes(file.type)) {
      setError('Only JPG and PNG files are allowed');
      return;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      setError(`File size exceeds 400KB limit (${(file.size / 1024).toFixed(1)}KB)`);
      return;
    }

    // Process the file
    setIsLoading(true);

    // Create a preview URL
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result);
      // Convert to base64 for storage or upload
      const base64String = reader.result;
      // Pass the image data to the parent component
      onImageChange(base64String);
      setIsLoading(false);
      setUploadSuccess(true);
      // Reset success message after 3 seconds
      setTimeout(() => {
        setUploadSuccess(false);
      }, 3000);
    };
    reader.onerror = () => {
      setError('Failed to read file');
      setIsLoading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setPreview('');
    onImageChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleExternalUrlChange = (url) => {
    if (!url) {
      setPreview('');
      onImageChange('');
      return;
    }

    setIsLoading(true);
    setError(null);

    // Check if URL is valid
    try {
      new URL(url);
    } catch (e) {
      setError('Please enter a valid URL');
      setIsLoading(false);
      return;
    }

    // Load image to validate URL
    const img = new Image();
    img.onload = () => {
      setPreview(url);
      onImageChange(url);
      setIsLoading(false);
      setUploadSuccess(true);
      setTimeout(() => {
        setUploadSuccess(false);
      }, 3000);
    };
    img.onerror = () => {
      setError('Failed to load image from URL');
      setIsLoading(false);
    };
    img.src = url;
  };

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Brand Logo *
      </label>
      
      {preview ? (
        <div className="relative mb-2 border rounded-lg overflow-hidden">
          <img 
            src={preview} 
            alt="Logo preview" 
            className="w-full h-32 object-contain bg-white p-2"
            onError={(e) => {
              setError('Error loading image');
              setPreview('');
              onImageChange('');
            }}
          />
          <button
            onClick={handleRemoveImage}
            className="absolute top-1 right-1 bg-gray-800 bg-opacity-70 text-white p-1 rounded-full hover:bg-opacity-100 transition-opacity"
            title="Remove image"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>
      ) : (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragging
              ? 'border-blue-500 bg-blue-50'
              : error
              ? 'border-red-300 bg-red-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <SafeIcon
                icon={isLoading ? FiImage : FiUpload}
                className={`text-xl ${
                  isLoading ? 'animate-pulse text-blue-500' : 'text-gray-500'
                }`}
              />
            </div>
            <p className="font-medium text-gray-800">
              {isLoading ? 'Processing...' : 'Drag & drop logo here'}
            </p>
            <p className="text-xs text-gray-500 mt-1 mb-3">
              JPG or PNG, max 400KB
            </p>
            <button
              type="button"
              onClick={() => fileInputRef.current.click()}
              disabled={isLoading}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                isLoading
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              } transition-colors`}
            >
              Select File
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              className="hidden"
              onChange={handleFileSelect}
              disabled={isLoading}
            />
          </div>
        </div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md flex items-center text-red-600 text-sm"
        >
          <SafeIcon icon={FiAlertCircle} className="mr-2 flex-shrink-0" />
          <span>{error}</span>
        </motion.div>
      )}

      {uploadSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md flex items-center text-green-600 text-sm"
        >
          <SafeIcon icon={FiCheckCircle} className="mr-2 flex-shrink-0" />
          <span>Logo uploaded successfully</span>
        </motion.div>
      )}

      <div className="mt-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Or enter logo URL
        </label>
        <input
          type="text"
          value={preview && !preview.startsWith('data:') ? preview : ''}
          onChange={(e) => handleExternalUrlChange(e.target.value)}
          placeholder="https://example.com/logo.png"
          className="w-full p-2 border rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <p className="mt-2 text-xs text-gray-500">
        Tip: For best results, use a square image with transparent background
      </p>
    </div>
  );
};

export default LogoUploader;